import { Box, Button } from "@mui/joy";
import { useCallback, useEffect, useRef, useState } from "react";
import { ProgramContentType } from "data/BlockComponent";
import { setProgramContentData } from "logic/logics";
import { sendMessage } from "utils/webview";
import useGetEventFromApp from "hooks/useGetEventFromApp";
import AudioProgressBar from "./AudioProgressBar";
import { theme } from "styles/theme";
import { PauseIcon, PlayIcon } from "./AudioButtonStatus";
import { useRecoilState } from "recoil";
import { activeAudioPlayerAtom } from "recoil/activeAudioPlayerAtom";
import _ from "lodash";
import { isPlaybackAtEnd } from "./audioReplayLogic";
import {
  decideStallRecovery,
  STALL_WATCHDOG_INTERVAL_MS,
} from "./audioStallLogic";

type PlayerStatus =
  | "idle"
  | "loading"
  | "playing"
  | "buffering"
  | "paused"
  | "finished"
  | "error";

export default function AudioButtonWebview({
  audioInstanceKey,
  audioUrl,
  initialTime,
  setData,
  blockIndex,
  lineIndex,
  cellIndex,
  isCoach,
  title,
  artist,
  artwork,
  extraAudioUrl,
  readonly,
  canReplay,
  isEnded,
}: {
  audioInstanceKey: string;
  audioUrl: string;
  initialTime: number;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  blockIndex: number;
  lineIndex: number;
  cellIndex: number;
  isCoach: boolean;
  title?: string;
  artist?: string;
  artwork?: string;
  extraAudioUrl?: string;
  readonly: boolean;
  canReplay?: boolean;
  isEnded?: boolean;
}) {
  const [playerStatus, setPlayerStatus] = useState<PlayerStatus>("idle");
  const [currentTime, setCurrentTime] = useState<number>(initialTime);
  const [duration, setDuration] = useState<number>(0);
  const [isSeeking, setIsSeeking] = useState(false);

  const [activeAudioKey, setActiveAudioKey] = useRecoilState(
    activeAudioPlayerAtom
  );
  const isActive = activeAudioKey === audioInstanceKey;
  const isPlaying = isActive && playerStatus === "playing";
  const showAsPlaying =
    isPlaying ||
    (isActive && playerStatus === "buffering") ||
    (isActive && playerStatus === "loading");
  // 전환 임계값 여유로직(EPS) 적용: 부동소수점/통신 지연으로 인한 조기/지연 전환 흔들림 완화
  const EPS = 0.25;
  const isLive =
    !!extraAudioUrl &&
    duration > 0 &&
    currentTime >= Math.max(0, duration - EPS);

  const showReplay =
    !!canReplay && !showAsPlaying && isPlaybackAtEnd(duration, currentTime);

  // 메시지 두절(stall) 감지용: 이 인스턴스의 마지막 audioPlayer 메시지 수신 시각과
  // 무응답 getState 질의 횟수. 종료를 한 번이라도 관측했는지(isEnded 재확인용)도 기록.
  const lastMessageAtRef = useRef<number | null>(null);
  const lastStampedMessageRef = useRef<any>(null);
  const unansweredStateQueriesRef = useRef(0);
  const hasEndedLocallyRef = useRef(false);
  // 감시 타이머 안에서 최신 값을 읽기 위한 스냅샷 (interval 재생성 방지)
  const stallStateRef = useRef({
    isActive,
    playerStatus,
    currentTime,
    duration,
  });
  stallStateRef.current = { isActive, playerStatus, currentTime, duration };

  useEffect(() => {
    if (isPlaying) {
      return;
    }

    setCurrentTime(initialTime);
  }, [initialTime, isPlaying]);

  useEffect(() => {
    if (!audioUrl || duration > 0) {
      return;
    }

    const audio = new Audio(audioUrl);
    audio.preload = "metadata";

    const handleMetadataLoaded = () => {
      if (isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handleError = () => {
      console.error("오디오 메타데이터를 불러오는 데 실패했습니다.", audioUrl);
    };

    // 이벤트 리스너 등록
    audio.addEventListener("loadedmetadata", handleMetadataLoaded);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("loadedmetadata", handleMetadataLoaded);
      audio.removeEventListener("error", handleError);
      // audio 객체에 대한 참조를 정리하여 메모리 누수 방지
      audio.src = "";
    };
  }, [audioUrl, duration]);

  const requestLoadAndPlay = useCallback(
    (startTime?: number) => {
      sendMessage(
        JSON.stringify({
          event: "audioAction",
          params: {
            action: "loadAndPlay",
            src: audioUrl,
            initialTime: startTime ?? currentTime,
            audioInstanceKey,
            title,
            artist,
            artwork,
            extraAudioUrl: extraAudioUrl,
          },
        })
      );
    },
    [
      audioUrl,
      currentTime,
      playerStatus,
      audioInstanceKey,
      title,
      artist,
      artwork,
      extraAudioUrl,
    ]
  );

  const requestPause = useCallback(() => {
    sendMessage(
      JSON.stringify({
        event: "audioAction",
        params: { action: "pause", audioInstanceKey },
      })
    );
  }, [audioInstanceKey]);

  const requestResume = useCallback(() => {
    sendMessage(
      JSON.stringify({
        event: "audioAction",
        params: {
          action: "resume",
          src: audioUrl,
          initialTime: currentTime,
          audioInstanceKey,
          title,
          artist,
          artwork,
          extraAudioUrl,
        },
      })
    );
  }, [
    audioUrl,
    currentTime,
    playerStatus,
    audioInstanceKey,
    title,
    artist,
    artwork,
    extraAudioUrl,
  ]);

  const requestSeek = useCallback(
    (seekTime: number) => {
      sendMessage(
        JSON.stringify({
          event: "audioAction",
          params: { action: "seek", audioInstanceKey, seekTime },
        })
      );
    },
    [audioInstanceKey]
  );

  // 네이티브 플레이어 상태 스냅샷 질의. 신버전 바이너리만 응답하며,
  // 구버전 바이너리는 unknown action 경고만 남기고 무시한다.
  const requestGetState = useCallback(() => {
    sendMessage(
      JSON.stringify({
        event: "audioAction",
        params: { action: "getState", audioInstanceKey },
      })
    );
  }, [audioInstanceKey]);

  const parsedMessageFromApp = useGetEventFromApp();
  useEffect(() => {
    if (
      parsedMessageFromApp?.event !== "audioPlayer" ||
      parsedMessageFromApp.params?.audioInstanceKey !== audioInstanceKey
    ) {
      return;
    }
    const {
      status: statusFromApp,
      currentTime: timeFromApp,
      duration: durationFromApp,
      seekCompleted,
    } = parsedMessageFromApp.params;

    // 이 effect는 메시지 외 dep 변경으로도 재실행되므로,
    // 수신 시각은 실제 새 메시지가 도착했을 때만 갱신한다 (stall 오판 방지)
    const isNewMessage = lastStampedMessageRef.current !== parsedMessageFromApp;
    if (isNewMessage) {
      lastStampedMessageRef.current = parsedMessageFromApp;
      lastMessageAtRef.current = Date.now();
      unansweredStateQueriesRef.current = 0;
    }

    const newStatus = (
      statusFromApp === "stopped" ? "paused" : statusFromApp
    ) as PlayerStatus;

    // 새 재생 로딩 중 도착한 finished는 직전 세션의 잔여 재전송분이다.
    // (정상 종료는 playing 상태에서 도착) 처리하면 방금 시작한 재생을
    // 종료 처리해 끊어버리므로 통째로 무시한다.
    if (newStatus === "finished" && playerStatus === "loading") {
      return;
    }

    setPlayerStatus(newStatus);

    // 페이지 리로드(30분 백그라운드 새로고침·렌더러 킬) 후에도 네이티브는 이
    // 인스턴스를 계속 재생 중일 수 있다. 활성 키가 비어 있으면 재입양해
    // 재생 UI·5초 저장·두절 감시가 다시 동작하게 한다. (다른 인스턴스가
    // 활성인 경우는 건드리지 않음 — 오탐 방지)
    if (
      activeAudioKey === null &&
      (newStatus === "playing" || newStatus === "buffering")
    ) {
      setActiveAudioKey(audioInstanceKey);
    }

    if (seekCompleted) {
      setIsSeeking(false);
    }

    if (!isSeeking) {
      if (typeof timeFromApp === "number" && timeFromApp > 0) {
        setCurrentTime(timeFromApp);
      }
    }

    if (typeof durationFromApp === "number" && durationFromApp > 0) {
      setDuration(durationFromApp);
    }

    // 화면이 꺼진 동안 진행 메시지가 끊기면(구버전 바이너리) 5초 저장이 멈추고, 복귀 후
    // 곧바로 일시정지하면 그 사이 청취한 시간이 기록되지 않는다. 시간이 실린 일시정지·정지
    // 상태(일시정지 버튼, gracefulStop, getState 복구)를 받으면 그 위치를 바로 기록한다.
    // 저장된 위치(initialTime)보다 앞선 값만 쓴다: finished 직후 오는 gracefulStop 응답이나
    // 뒤로 탐색 후 일시정지가 기록을 되돌리지 않게 한다. isEnded 판정은 5초 저장과 동일.
    // readonly·코치 뷰는 getState 재동기화로도 이 메시지가 도달할 수 있으므로 제외한다.
    if (
      isNewMessage &&
      newStatus === "paused" &&
      typeof timeFromApp === "number" &&
      timeFromApp > initialTime &&
      !isCoach &&
      !readonly
    ) {
      const isEndedByPosition = duration > 0 && duration - 30 <= timeFromApp;
      if (isEndedByPosition) {
        hasEndedLocallyRef.current = true;
      }
      setProgramContentData({
        setData,
        blockIndex,
        lineIndex,
        cellIndex,
        newlyAddedData: {
          currentTime: timeFromApp,
          id: "meditation_time",
          value: `${Math.floor((timeFromApp + 10 || 60) / 60)}`,
          ...(isEndedByPosition && { isEnded: true }),
        },
      });
    }

    if (newStatus === "finished") {
      hasEndedLocallyRef.current = true;
      setActiveAudioKey(null);

      // 복구 경로(getState 응답 등)에선 로컬 currentTime이 실제 종료 지점보다
      // 한참 뒤처져 있을 수 있으므로, 메시지에 실린 종료 시각을 우선 사용한다.
      const endTime =
        typeof timeFromApp === "number" && timeFromApp > 0
          ? timeFromApp
          : currentTime;

      if (!isCoach && !readonly) {
        setProgramContentData({
          setData,
          blockIndex,
          lineIndex,
          cellIndex,
          newlyAddedData: {
            currentTime: endTime,
            ...(endTime > 0 && {
              id: "meditation_time",
              value: `${Math.floor((endTime + 10 || 60) / 60)}`,
            }),
            isEnded: true,
          },
        });
      }
    }
  }, [
    parsedMessageFromApp,
    audioInstanceKey,
    isSeeking,
    activeAudioKey,
    setActiveAudioKey,
    isCoach,
    readonly,
    playerStatus,
    setData,
    blockIndex,
    lineIndex,
    cellIndex,
    currentTime,
    duration,
    initialTime,
  ]);

  useEffect(() => {
    const isTimeToSave =
      isActive &&
      playerStatus === "playing" &&
      Math.floor(currentTime) % 5 === 0;

    if (isTimeToSave) {
      const isEnded =
        duration > 0 && currentTime > 0 && duration - 30 <= currentTime;
      if (isEnded) {
        hasEndedLocallyRef.current = true;
      }
      setProgramContentData({
        setData,
        blockIndex,
        lineIndex,
        cellIndex,
        newlyAddedData: {
          currentTime,
          ...(currentTime > 0 && {
            id: "meditation_time",
            value: `${Math.floor((currentTime + 10 || 60) / 60)}`,
          }),
          ...(isEnded && { isEnded: true }),
        },
      });
    }
  }, [
    isActive,
    playerStatus,
    currentTime,
    duration,
    setData,
    blockIndex,
    lineIndex,
    cellIndex,
  ]);

  useEffect(() => {
    return () => {
      if (activeAudioKey === audioInstanceKey) {
        sendMessage(
          JSON.stringify({
            event: "audioAction",
            params: { action: "gracefulStop", audioInstanceKey },
          })
        );
        setActiveAudioKey(null);
      }
    };
  }, [activeAudioKey, audioInstanceKey, setActiveAudioKey]);

  // 빠른 연속 토글 방지(디바운스)
  const lastToggleRef = useRef<number>(0);
  const canToggle = useCallback(() => {
    const now = Date.now();
    if (now - lastToggleRef.current < 250) return false;
    lastToggleRef.current = now;
    return true;
  }, []);

  const start = useCallback(() => {
    if (!canToggle()) return;
    lastMessageAtRef.current = Date.now();
    unansweredStateQueriesRef.current = 0;
    setActiveAudioKey(audioInstanceKey);
    if (playerStatus === "paused") {
      requestResume();
    } else {
      requestLoadAndPlay();
    }
  }, [
    setActiveAudioKey,
    audioInstanceKey,
    playerStatus,
    requestLoadAndPlay,
    canToggle,
  ]);

  // 다시주기 상태에서 끝까지 들은 오디오를 처음부터 다시 재생
  const replay = useCallback(() => {
    if (!canToggle()) return;
    lastMessageAtRef.current = Date.now();
    unansweredStateQueriesRef.current = 0;
    setCurrentTime(0);
    if (!isCoach) {
      // initialTime prop을 0으로 선기록해 비재생 동기화 effect의 스냅백 방지.
      // merge 방식이라 isEnded·meditation_time은 보존됨 (완료 게이팅 불변).
      setProgramContentData({
        setData,
        blockIndex,
        lineIndex,
        cellIndex,
        newlyAddedData: { currentTime: 0 },
      });
    }
    setActiveAudioKey(audioInstanceKey);
    requestLoadAndPlay(0);
  }, [
    canToggle,
    isCoach,
    setData,
    blockIndex,
    lineIndex,
    cellIndex,
    setActiveAudioKey,
    audioInstanceKey,
    requestLoadAndPlay,
  ]);

  const stop = useCallback(() => {
    if (!canToggle()) return;
    requestPause();
  }, [requestPause, canToggle]);

  const handleSeekStart = () => setIsSeeking(true);
  const handleSeekChange = (newTime: number) => setCurrentTime(newTime);
  const handleSeekEnd = (newTime: number) => {
    requestSeek(newTime);
  };

  const setAudioEnded = useCallback(() => {
    setData((data) => {
      if (data) {
        const data_temp = _.cloneDeep(data);
        const currentContent =
          data_temp[blockIndex].lines[lineIndex][cellIndex].content || {};

        data_temp[blockIndex].lines[lineIndex][cellIndex].content = {
          ...currentContent,
          isEnded: true,
        };
        if (!_.isEqual(data, data_temp)) {
          return data_temp;
        }
        return data;
      }
      return data;
    });
  }, [setData, blockIndex, lineIndex, cellIndex]);

  // 두절 상태에서 트랙이 이미 끝났다고 판정된 경우의 종료 확정 처리.
  // 네이티브는 백그라운드에서 끝까지 재생을 마친 뒤 침묵했으므로,
  // 종료 통지("finished")를 받은 것과 동일하게 상태를 정리한다.
  const finalizeStalledPlayback = useCallback(() => {
    const { duration: knownDuration } = stallStateRef.current;
    hasEndedLocallyRef.current = true;
    setPlayerStatus("finished");
    setCurrentTime(knownDuration);
    setActiveAudioKey(null);
    // 네이티브에 남은 세션(Ended 상태 트랙·미디어 알림) 정리. 세션이 없으면 무시됨.
    sendMessage(
      JSON.stringify({
        event: "audioAction",
        params: { action: "gracefulStop", audioInstanceKey },
      })
    );
    if (!isCoach && !readonly) {
      setProgramContentData({
        setData,
        blockIndex,
        lineIndex,
        cellIndex,
        newlyAddedData: {
          currentTime: knownDuration,
          ...(knownDuration > 0 && {
            id: "meditation_time",
            value: `${Math.floor((knownDuration + 10 || 60) / 60)}`,
          }),
          isEnded: true,
        },
      });
    }
  }, [
    audioInstanceKey,
    isCoach,
    readonly,
    setData,
    blockIndex,
    lineIndex,
    cellIndex,
    setActiveAudioKey,
  ]);

  const runStallCheck = useCallback(() => {
    const snapshot = stallStateRef.current;
    const action = decideStallRecovery({
      isActive: snapshot.isActive,
      playerStatus: snapshot.playerStatus,
      lastMessageAtMs: lastMessageAtRef.current,
      nowMs: Date.now(),
      lastKnownTimeSec: snapshot.currentTime,
      durationSec: snapshot.duration,
      isDocumentVisible: document.visibilityState === "visible",
      unansweredStateQueries: unansweredStateQueriesRef.current,
      isLiveTrack: !!extraAudioUrl,
    });

    if (action === "finalize") {
      finalizeStalledPlayback();
    } else if (action === "queryState") {
      unansweredStateQueriesRef.current += 1;
      requestGetState();
    } else if (action === "downgradeToPaused") {
      // 구버전 바이너리(getState 미지원)에서 갇힌 "재생 중" UI를
      // 재생 버튼이 동작하는 일시정지 상태로 되돌린다.
      unansweredStateQueriesRef.current = 0;
      setPlayerStatus("paused");
    }
  }, [extraAudioUrl, finalizeStalledPlayback, requestGetState]);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    const timerId = setInterval(runStallCheck, STALL_WATCHDOG_INTERVAL_MS);
    return () => clearInterval(timerId);
  }, [isActive, runStallCheck]);

  const handleVisibilityChange = useCallback(() => {
    // 안드로이드 WebView: 백그라운드 전환은 종료로 간주하지 않음
    // (iOS 대응과 동일하게 종료 표시를 제거하여 오동작/전환 경합 방지)
    // 포그라운드 복귀 시에는 두절 여부를 즉시 점검해 놓친 종료 통지를 복구한다.
    if (document.visibilityState === "visible") {
      runStallCheck();
    }
  }, [runStallCheck]);

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  // 페이지 리로드(백그라운드 30분 초과 새로고침, 렌더러 킬 복구) 후 재동기화:
  // 네이티브가 종료 상태로 침묵 중이면 mount 시점 질의로 "finished"를 받아온다.
  useEffect(() => {
    requestGetState();
  }, [requestGetState]);

  // 서버 refetch가 낡은 데이터로 로컬 콘텐츠를 덮어써도(저장 실패·경합)
  // 이 세션에서 관측한 종료 사실은 다시 기록한다. isEnded는 한 번 true가 되면
  // 유지되는 값이다 (audioReplayLogic 참조).
  useEffect(() => {
    if (hasEndedLocallyRef.current && !isEnded && !isCoach && !readonly) {
      setAudioEnded();
    }
  }, [isEnded, isCoach, readonly, setAudioEnded]);

  return (
    <Box>
      <AudioProgressBar
        currentTime={currentTime}
        duration={duration}
        onSeekStart={handleSeekStart}
        onSeekChange={handleSeekChange}
        onSeekEnd={handleSeekEnd}
        isPlaying={showAsPlaying}
        isLive={isLive}
      />
      <Button
        variant={showAsPlaying ? "Outlined" : "Solid"}
        color="primary"
        fullWidth
        onClick={() =>
          showAsPlaying ? stop() : showReplay ? replay() : start()
        }
        size={"lg"}
        sx={{
          fontSize: "16px",
          fontWeight: 700,
          justifyContent: "center",
          "& .MuiButton-startDecorator": {
            marginRight: 0,
            color: readonly
              ? theme.vars.palette.Content.SuperWeak
              : showAsPlaying
                ? theme.vars.palette.Content.Highlight
                : "white",
          },
          gap: 1,
        }}
        disabled={readonly}
        startDecorator={showAsPlaying ? <PauseIcon /> : <PlayIcon />}
      >
        {showAsPlaying ? "일시정지" : showReplay ? "다시 재생하기" : "재생하기"}
      </Button>
    </Box>
  );
}
